import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { isAgentRecordLocked } from "../utils/appAuth";
import RequestMissingDocumentsModal from "./RequestMissingDocumentsModal";

export function listingOptionLabel(listing) {
  const num = listing?.listingNumber ?? "";
  const addr = [
    listing?.address?.streetNumber,
    listing?.address?.streetName,
    listing?.address?.unit,
  ]
    .filter(Boolean)
    .join(" ");
  return addr ? `#${num} — ${addr}` : `Listing #${num}`;
}

const canPreviewFile = (file) => {
  const mime = String(file?.mimeType || "").toLowerCase();
  const name = String(file?.originalName || "").toLowerCase();
  return (
    mime.startsWith("image/") ||
    mime === "application/pdf" ||
    /\.(pdf|png|jpe?g|webp)$/.test(name)
  );
};

function rememberedCategory(record) {
  const saved = String(record?.lastDocumentCategory || "").trim();
  if (saved) return saved;
  const packs = Array.isArray(record?.documentPacks) ? record.documentPacks : [];
  let best = "";
  let bestTime = 0;
  for (const pack of packs) {
    for (const file of pack.files || []) {
      const t = file.uploadedAt ? new Date(file.uploadedAt).getTime() : 0;
      if (pack.category && t >= bestTime) {
        bestTime = t;
        best = pack.category;
      }
    }
  }
  if (best) return best;
  const withFiles = packs.find((pack) => (pack.files || []).length);
  return withFiles?.category || "";
}

const ListingDocumentsSection = ({ listings }) => {
  const { isAgent, isSuperUser, isAdmin, isBroker } = useRole();
  const [searchParams] = useSearchParams();
  const canManageFiles = isAgent || isSuperUser || isAdmin || isBroker;
  const canRequestMissing = isSuperUser || isAdmin;
  const [categories, setCategories] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [columns, setColumns] = useState([]);
  const [savedFiles, setSavedFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestItemIds, setRequestItemIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [openingId, setOpeningId] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [lockedForAgent, setLockedForAgent] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const documentsLocked =
    isAgent &&
    (lockedForAgent || isAgentRecordLocked(selectedListing?.listing, "agent"));
  const canRemoveSavedFiles =
    !isBroker && canManageFiles && !documentsLocked;

  const listingOptions = useMemo(
    () =>
      (listings || []).map((listing) => ({
        value: listing._id,
        label: listingOptionLabel(listing),
        listing,
      })),
    [listings]
  );

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.label })),
    [categories]
  );

  useEffect(() => {
    const listingId = searchParams.get("listing");
    const categoryId = searchParams.get("category");
    if (listingId && listingOptions.length) {
      const match = listingOptions.find(
        (option) => String(option.value) === String(listingId)
      );
      if (match) setSelectedListing(match);
    }
    if (categoryId && categoryOptions.length) {
      const match = categoryOptions.find((option) => option.value === categoryId);
      if (match) setSelectedCategory(match);
    }
  }, [searchParams, listingOptions, categoryOptions]);

  useEffect(() => {
    const urlCategory = searchParams.get("category");
    const urlListing = searchParams.get("listing");
    if (
      urlCategory &&
      urlListing &&
      String(urlListing) === String(selectedListing?.value)
    ) {
      return;
    }
    if (!selectedListing) {
      setSelectedCategory(null);
      return;
    }
    if (!categoryOptions.length) return;
    const saved = rememberedCategory(selectedListing.listing);
    const match = saved
      ? categoryOptions.find((option) => option.value === saved)
      : null;
    setSelectedCategory(match || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListing?.value, categoryOptions]);

  useEffect(() => {
    setRequestModalOpen(false);
    setRequestItemIds([]);
    setSelectedDocument(null);
    setLockedForAgent(false);
  }, [selectedListing?.value, selectedCategory?.value]);

  const displayedFiles = useMemo(
    () => [...savedFiles, ...pendingFiles],
    [savedFiles, pendingFiles]
  );

  const closePreview = () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const fetchFileBlob = async (file, view) => {
    const res = await axiosInstance.get(
      `/listing-documents/${selectedListing.value}/files/${file.fileId}`,
      {
        params: { category: selectedCategory.value, view: view ? "1" : undefined },
        responseType: "blob",
        timeout: 120000,
      }
    );
    const mime =
      file.mimeType ||
      res.headers["content-type"] ||
      "application/octet-stream";
    return new Blob([res.data], { type: mime });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/listing-documents/categories");
        setCategories(res.data.categories || []);
      } catch (err) {
        toast.error(
          err.response?.data?.message || "Could not load document categories."
        );
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadPack = async () => {
      if (!selectedListing || !selectedCategory) {
        setColumns([]);
        setSavedFiles([]);
        setPendingFiles([]);
        return;
      }
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/listing-documents/${selectedListing.value}`,
          { params: { category: selectedCategory.value } }
        );
        setColumns(res.data.columns || []);
        setSavedFiles(res.data.files || []);
        setPendingFiles([]);
        setLockedForAgent(Boolean(res.data.lockedForAgent));
      } catch (err) {
        setColumns([]);
        setSavedFiles([]);
        setPendingFiles([]);
        toast.error(
          err.response?.data?.message ||
            "Could not load documents for this listing."
        );
      } finally {
        setLoading(false);
      }
    };
    loadPack();
  }, [selectedListing, selectedCategory]);

  const updateItem = (updated) => {
    setColumns((prev) =>
      prev.map((col) => col.map((item) => (item.id === updated.id ? updated : item)))
    );
  };

  const persistFiles = async (files) => {
    if (!selectedDocument?.value) {
      throw new Error("Select the document you are uploading.");
    }
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("category", selectedCategory.value);
    formData.append("itemId", selectedDocument.value);
    const res = await axiosInstance.post(
      `/listing-documents/${selectedListing.value}/files`,
      formData,
      { timeout: 180000 }
    );
    setSavedFiles(res.data.files || []);
    const uploadedItemId = selectedDocument.value;
    setColumns((prev) => {
      const source = res.data.columns || prev;
      return source.map((col) =>
        col.map((item) =>
          item.id === uploadedItemId ? { ...item, checked: true } : item
        )
      );
    });
    if (typeof res.data.lockedForAgent === "boolean") {
      setLockedForAgent(res.data.lockedForAgent);
    }
    if (selectedListing?.listing && selectedCategory?.value) {
      selectedListing.listing.lastDocumentCategory = selectedCategory.value;
    }
  };

  const handleUpload = async (selectedFiles) => {
    if (!selectedFiles?.length || !selectedListing || !selectedCategory) return;
    if (!selectedDocument?.value) {
      toast.error("Select the document you are uploading first.");
      return;
    }
    try {
      setSaving(true);
      await persistFiles(selectedFiles);
      setSelectedDocument(null);
      toast.success(
        selectedFiles.length === 1
          ? "Document saved. Compliance review will run automatically and email the agent."
          : "Documents saved. Compliance review will run automatically and email the agent.",
      );
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Could not save files. Click Save to try again.";
      if (err.response?.data?.code !== "WRONG_FORM") {
        const next = selectedFiles.map((file, index) => ({
          fileId: `pending-${Date.now()}-${index}-${file.name}`,
          originalName: file.name,
          mimeType: file.type,
          pending: true,
          file,
        }));
        setPendingFiles((prev) => [...prev, ...next]);
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedListing || !selectedCategory) return;
    if (!pendingFiles.length) {
      toast.success("Documents are already saved.");
      return;
    }
    if (!selectedDocument?.value) {
      toast.error("Select the document you are uploading first.");
      return;
    }
    try {
      setSaving(true);
      await persistFiles(pendingFiles.map((row) => row.file));
      setPendingFiles([]);
      setSelectedDocument(null);
      toast.success(
        "Documents saved. Compliance review will run automatically and email the agent.",
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save files.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (file) => {
    if (file.pending) {
      setPendingFiles((prev) => prev.filter((row) => row.fileId !== file.fileId));
      return;
    }
    if (!selectedListing || !selectedCategory) return;
    if (documentsLocked) {
      toast.error(
        "This listing has been reviewed. You cannot remove uploaded documents."
      );
      return;
    }
    try {
      setRemovingId(file.fileId);
      const res = await axiosInstance.delete(
        `/listing-documents/${selectedListing.value}/files/${file.fileId}`,
        { params: { category: selectedCategory.value } }
      );
      setSavedFiles(res.data.files || []);
      if (res.data.columns) setColumns(res.data.columns);
      if (typeof res.data.lockedForAgent === "boolean") {
        setLockedForAgent(res.data.lockedForAgent);
      }
      toast.success("File removed.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not remove that file.");
    } finally {
      setRemovingId("");
    }
  };

  const handleCheck = async (item, checked) => {
    if (!selectedListing || !selectedCategory) return;
    try {
      const res = await axiosInstance.patch(
        `/listing-documents/${selectedListing.value}/items/${item.id}`,
        { category: selectedCategory.value, checked }
      );
      updateItem(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update checkbox.");
    }
  };

  const handleOtherText = async (item, otherText) => {
    if (!selectedListing || !selectedCategory) return;
    try {
      const res = await axiosInstance.patch(
        `/listing-documents/${selectedListing.value}/items/${item.id}`,
        { category: selectedCategory.value, otherText }
      );
      updateItem(res.data);
    } catch (_) {}
  };

  const openPreview = (url, file, mimeType) => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = url;
    setPreview({
      url,
      name: file.originalName || "document",
      mimeType: mimeType || file.mimeType || "",
    });
  };

  const handleView = async (file) => {
    if (file.pending && file.file) {
      if (!canPreviewFile(file)) {
        handleDownload(file);
        return;
      }
      openPreview(window.URL.createObjectURL(file.file), file, file.file.type);
      return;
    }
    if (!canPreviewFile(file)) {
      handleDownload(file);
      return;
    }
    try {
      setOpeningId(file.fileId);
      const blob = await fetchFileBlob(file, true);
      openPreview(window.URL.createObjectURL(blob), file, blob.type);
    } catch (err) {
      toast.error("Could not open that file.");
    } finally {
      setOpeningId("");
    }
  };

  const handleDownload = async (file) => {
    try {
      setOpeningId(file.fileId);
      const blob =
        file.pending && file.file
          ? file.file
          : await fetchFileBlob(file, false);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originalName || "document";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download that file.");
    } finally {
      setOpeningId("");
    }
  };

  const documentOptions = useMemo(
    () =>
      columns
        .flat()
        .filter(Boolean)
        .map((item) => {
          const extra = String(item.otherText || "").trim();
          const label = String(item.label || "").replace(/\.$/, "");
          return {
            value: item.id,
            label: extra ? `${label} (${extra})` : label,
            item,
          };
        }),
    [columns]
  );

  const missingItems = useMemo(
    () =>
      columns
        .flat()
        .filter(
          (item) =>
            item &&
            !item.checked &&
            !(item.isOther && !String(item.otherText || "").trim())
        ),
    [columns]
  );
  const missingCount = missingItems.length;

  const openRequestModal = () => {
    if (!selectedListing || !selectedCategory) return;
    setRequestItemIds(missingItems.map((item) => item.id));
    setRequestModalOpen(true);
  };

  const closeRequestModal = () => {
    if (sending) return;
    setRequestModalOpen(false);
    setRequestItemIds([]);
  };

  const handleCategoryChange = (option) => {
    setSelectedCategory(option);
    if (!selectedListing?.value || !option?.value) return;
    if (selectedListing.listing) {
      selectedListing.listing.lastDocumentCategory = option.value;
    }
    axiosInstance
      .patch(`/listing-documents/${selectedListing.value}/category`, {
        category: option.value,
      })
      .catch(() => {});
  };

  const handleRequestMissing = async (payload = {}) => {
    if (!selectedListing || !selectedCategory) return;
    const otherMessage = String(payload.otherMessage || "").trim();
    if (!requestItemIds.length && !otherMessage) {
      toast.error("Select at least one document or type an Other message.");
      return;
    }
    setSending(true);
    try {
      const res = await axiosInstance.post(
        `/listing-documents/${selectedListing.value}/request-missing`,
        {
          category: selectedCategory.value,
          missingItemIds: requestItemIds,
          otherMessage,
        }
      );
      toast.success(res.data.message || "Request sent to the agent.");
      setRequestModalOpen(false);
      setRequestItemIds([]);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not send the missing-documents email."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">Documents</h2>
      <p className="text-sm text-gray-600 mb-6">
        {isBroker
          ? "Select a listing and category to view uploaded documents. Use View or Download on each file."
          : "Select a listing, category, and document, then upload. The selected document is checked off automatically after the file is saved."}
        {canRequestMissing
          ? " Super Users can email the agent to request selected unchecked documents."
          : ""}
        {documentsLocked
          ? " This listing has been reviewed, so saved documents can no longer be removed."
          : ""}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Listing <span className="text-red-600">*</span>
          </label>
          <Select
            options={listingOptions}
            value={selectedListing}
            onChange={setSelectedListing}
            isClearable
            placeholder="Select listing"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Category <span className="text-red-600">*</span>
          </label>
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={handleCategoryChange}
            isClearable
            placeholder="Select category"
          />
        </div>
      </div>

      {selectedListing && selectedCategory && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-blue-900">
              {selectedCategory.label}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {canManageFiles && (
                <>
                  <div className="min-w-[220px] w-full sm:w-72">
                    <Select
                      options={documentOptions}
                      value={selectedDocument}
                      onChange={setSelectedDocument}
                      isClearable
                      placeholder="Select document to upload"
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      e.target.value = "";
                      if (selected.length) handleUpload(selected);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedDocument?.value) {
                        toast.error("Select the document you are uploading first.");
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                    disabled={saving || !selectedDocument?.value}
                    className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? "Checking form..." : "Upload"}
                  </button>
                  {pendingFiles.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  ) : null}
                </>
              )}
              {canRequestMissing && (
                <button
                  type="button"
                  onClick={openRequestModal}
                  disabled={sending}
                  className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Email agent for missing documents ({missingCount})
                </button>
              )}
            </div>
          </div>

          <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-sm font-semibold text-blue-900 mb-2">
              Uploaded files ({displayedFiles.length})
            </p>
            {displayedFiles.length === 0 ? (
              <p className="text-sm text-blue-800">
                No files uploaded yet. Select a document, then click Upload.
              </p>
            ) : (
              <ul className="divide-y divide-blue-100 bg-white rounded-md border border-blue-100">
                {displayedFiles.map((file) => (
                  <li
                    key={file.fileId}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="text-sm text-gray-800 break-all">
                      {file.originalName || "Uploaded file"}
                      {file.itemLabel ? (
                        <span className="ml-2 text-xs font-medium text-blue-800">
                          {file.itemLabel.replace(/\.$/, "")}
                        </span>
                      ) : null}
                      {file.pending ? (
                        <span className="ml-2 text-xs font-medium text-amber-700">
                          Not saved
                        </span>
                      ) : null}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        className="text-sm font-medium text-blue-900 hover:underline disabled:text-gray-400"
                        disabled={openingId === file.fileId}
                        onClick={() => handleView(file)}
                      >
                        {openingId === file.fileId ? "Opening..." : "View"}
                      </button>
                      <button
                        type="button"
                        className="text-sm text-gray-600 hover:underline disabled:text-gray-400"
                        disabled={openingId === file.fileId}
                        onClick={() => handleDownload(file)}
                      >
                        Download
                      </button>
                      {canManageFiles &&
                        !isBroker &&
                        (file.pending || canRemoveSavedFiles) && (
                        <button
                          type="button"
                          className="text-sm text-red-700 hover:underline disabled:text-gray-400"
                          disabled={removingId === file.fileId || saving}
                          onClick={() => handleRemove(file)}
                        >
                          {removingId === file.fileId ? "Removing..." : "Remove"}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">Loading checklist...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
              {columns.map((col, colIdx) => (
                <div key={colIdx} className="space-y-3">
                  {col.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-2 p-1.5 rounded-md cursor-pointer ${
                        selectedDocument?.value === item.id
                          ? "bg-blue-50 ring-1 ring-blue-300"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        setSelectedDocument({
                          value: item.id,
                          label: String(item.label || "").replace(/\.$/, ""),
                          item,
                        })
                      }
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={Boolean(item.checked)}
                        onChange={(e) => handleCheck(item, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm text-gray-800 leading-snug">
                          {item.label}
                        </span>
                        {item.isOther && (
                          <input
                            type="text"
                            value={item.otherText || ""}
                            onChange={(e) =>
                              updateItem({ ...item, otherText: e.target.value })
                            }
                            onBlur={(e) =>
                              handleOtherText(item, e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Specify"
                            className="mt-1 block w-full text-xs rounded-md border-gray-300 shadow-sm"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <RequestMissingDocumentsModal
        open={requestModalOpen}
        items={missingItems}
        selectedIds={requestItemIds}
        sending={sending}
        categoryLabel={selectedCategory?.label}
        onToggle={(id) =>
          setRequestItemIds((prev) =>
            prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
          )
        }
        onSelectAll={() => setRequestItemIds(missingItems.map((item) => item.id))}
        onClearAll={() => setRequestItemIds([])}
        onCancel={closeRequestModal}
        onSend={handleRequestMissing}
      />

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
              <h4 className="font-semibold text-blue-900 truncate">
                {preview.name}
              </h4>
              <button
                type="button"
                onClick={closePreview}
                className="px-3 py-1.5 text-sm bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Close
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-gray-100">
              {String(preview.mimeType || "").startsWith("image/") ? (
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="w-full h-full border-0 bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingDocumentsSection;
